import { createSlice } from "@reduxjs/toolkit";
import { getAdTypes, getCampaigns, getCampaignsByPage, getCreatives, getDynamicText, getFontList, getTemplate, getTemplateCategory, getTemplateCreatives, getVersions, linkFeedPathToDynamicText, searchFontForDynamicText, setHtmlCreativesPreview } from "./thunk";
import { cleanString, getStyleValue } from "../helper.js";
import { isJsonPath } from "./helper";
import { facebook } from "./facebook/facebook.js";
import { sortByWidth } from "../helper.js";

export const initialState = {
  allLibrariesPage: 1,
  allLibraries: null,
  allLibrariesLoading: false,
  allLibrariesHasMore: true,
  allLibrariesFirstLoad: false,
  allLibrariesShowLoader: false,
  allLibrariesErr: false,
  allLibrariesErrMsg: null,
  campaignUsers: null,
  adTypes: null,
  filter: {
    query: null,
    publish: null,
    user: null,
    market: null,
    sizes: null,
  },
  deleteCampaignId: null,
  currentCampaign: null,
  versions: null,
  currentVersion: null,
  versionDetails: {
    _id: "",
    name: "",
    activityType: { name: "", value: "" },
    landingPage: "",
    campaignId: "",
    sizes: []
  },
  creatives: null,
  filteredCreatives: [],
  scrolledCreatives: [],
  creativesHasMore: true,
  creativesLoading: false,
  creativesFirstLoad: true,
  creativesPage: 1,
  deleteCreativeId: null,
  duplicateCreative: null,
  creativeOpenedInFigma: null,
  comments: null,
  commentRelationId: null,
  commentType: null,
  isTemplateLoading: false,
  loadedHtmlZip: null,
  templateCategories: [],
  templates: null,
  templateFilter: {
    query: null,
    campaignId: null,
  },
  selectedTemplate: null,
  templateName: null,
  filteredTemplateCreatives: [],
  // Dynamic Text
  selectedCreative: null,
  dynamicText: null,
  activeDynamicText: null,
  isApplyAll: false,
  creativeLevelFilter: 'all',
  dynamicFontList: null,
  filteredFontList: null,
  fontNotUploaded: false,
  fontNotUploadedName: [],
  fontUpdated: false,
  dynamicVideoError: false,
  dynamicVideoErrorMsg: '',
  selectedFeedConnection: null,
  connectedFeed: null,
  connectedFeedName: null,
  selectedTag: null,
  campaignLoader: false,
  createCampaignErr: false,
  createCampaignErrMsg: null,
  settingsErr: false,
  settingsErrMsg: null,
  duplicateErr: false,
  duplicateErrMsg: null,
  // Facebook
  ...facebook.state,
};

const campaignSlice = createSlice({
  name: "Campaign",
  initialState,
  reducers: {
    setAllLibrariesPage(state, action) {
      state.allLibrariesPage = action.payload;
    },
    getAllLibrariesSuccess(state, action) {
      state.allLibraries = action.payload;
    },
    setAllLibrariesLoading(state, action) {
      state.allLibrariesLoading = action.payload;
    },
    setAllLibrariesHasMore(state, action) {
      state.allLibrariesHasMore = action.payload;
    },
    setAllLibrariesFirstLoad(state, action) {
      state.allLibrariesFirstLoad = action.payload;
    },
    setAllLibrariesShowLoader(state, action) {
      state.allLibrariesShowLoader = action.payload;
    },
    getAllLibrariesErr(state, action) {
      state.allLibrariesErr = true;
      state.allLibrariesErrMsg = action.payload;
    },
    setCampaignUsers(state, action) {
      state.campaignUsers = action.payload;
    },
    setAdTypes(state, action) {
      state.adTypes = action.payload;
    },
    setFilter(state, action) {
      state.filter = action.payload;
    },
    resetFilter(state) {
      state.filter = {
        query: null,
        publish: null,
        user: null,
        market: null,
        sizes: null,
      }
    },
    setDeleteCampaignId(state, action) {
      state.deleteCampaignId = action.payload;
    },
    setCurrentCampaign(state, action) {
      state.currentCampaign = action.payload;
    },
    getVersionsSuccess(state, action) {
      state.versions = action.payload;
    },
    setCurrentVersion(state, action) {
      state.currentVersion = action.payload;
    },
    setVersionDetails(state, action) {
      if (action.payload) {
        state.versionDetails = {
          _id: action.payload._id,
          name: action.payload.name,
          activityType: action.payload.activityType,
          landingPage: action.payload.landingPage,
          campaignId: action.payload.campaignId,
          sizes: action.payload.sizes
        };
      } else {
        if (state.currentVersion !== null) {
          state.versionDetails = {
            _id: state.currentVersion._id,
            name: state.currentVersion.name,
            activityType: state.currentVersion.activityType,
            landingPage: state.currentVersion.landingPage,
            campaignId: state.currentVersion.campaignId,
            sizes: state.currentVersion.sizes
          };
        }
      }

    },
    resetVersionDetails(state,) {
      state.versionDetails = {
        _id: "",
        name: "",
        activityType: { name: "", value: "" },
        landingPage: "",
        campaignId: "",
        sizes: []
      }
    },
    setCreatives(state, action) {
      state.creatives = action.payload;
    },
    setFilteredCreatives(state, action) {
      state.filteredCreatives = action.payload;
    },
    setScrolledCreatives(state, action) {
      state.scrolledCreatives = action.payload;
    },
    updateCreatives(state, action) {
      const creatives = [];
      let i = 0;
      do {
        const creative = state.creatives[i];
        if (action.payload._id === creative._id) {
          creatives.push({...action.payload});
        } else {
          creatives.push({...creative});
        }
        i++;
      } while (i < state.creatives.length);
      state.creatives = creatives;
    },
    updateScrolledCreatives(state, action) {
      const scrolledCreatives = [];
      let i = 0;
      do {
        const creative = state.scrolledCreatives[i];
        if (action.payload._id === creative._id) {
          scrolledCreatives.push({...action.payload});
        } else {
          scrolledCreatives.push({...creative});
        }
        i++;
      } while (i < state.scrolledCreatives.length);
      state.scrolledCreatives = scrolledCreatives;
    },
    setCreativesHasMore(state, action) {
      state.creativesHasMore = action.payload;
    },
    setCreativesLoading(state, action) {
      state.creativesLoading = action.payload;
    },
    setCreativesFirstLoad(state, action) {
      state.creativesFirstLoad = action.payload;
    },
    setCreativesPage(state, action) {
      state.creativesPage = action.payload;
    },
    setDeleteCreativeId(state, action) {
      state.deleteCreativeId = action.payload;
    },
    setDuplicateCreative(state, action) {
      state.duplicateCreative = action.payload;
    },
    setCreativeOpenedInFigma(state, action) {
      state.creativeOpenedInFigma = action.payload;
    },
    setSelectedCreative(state, action) {
      state.selectedCreative = action.payload;
    },
    setCreativeApprovement(state, action) {
      const updatedCreatives = [];
      let i = 0;
      do {
        const creative = state.filteredCreatives[i]
        if (action.payload.some(id => id === creative._id)) {
          let foundCreative = { ...creative };
          foundCreative.approvement = !foundCreative.approvement;
          updatedCreatives.push(foundCreative);
        } else {
          updatedCreatives.push(creative);
        }
        i++;
      } while (i < state.filteredCreatives.length);
      state.filteredCreatives = updatedCreatives;
    },
    setComments(state, action) {
      state.comments = action.payload;
    },
    setCommentRelationId(state, action) {
      state.commentRelationId = action.payload;
    },
    setCommentType(state, action) {
      state.commentType = action.payload;
    },
    setCreativeCommentCount(state, action) {
      const updatedCreatives = [];
      let i = 0;
      do {
        const creative = state.filteredCreatives[i];
        if (creative._id === action.payload) {
          let foundCreative = { ...creative };
          foundCreative.comments += 1;
          updatedCreatives.push(foundCreative);
        } else {
          updatedCreatives.push(creative);
        }
        i++;
      } while (i < state.filteredCreatives.length);
      state.filteredCreatives = updatedCreatives;
    },
    setTemplateLoader(state, action) {
      state.isTemplateLoading = action.payload;
    },
    setTemplates(state, action) {
      state.templates = action.payload;
    },
    setLoadedHtmlZip(state, action) {
      state.loadedHtmlZip = action.payload;
    },
    setTemplateCategories(state, action) {
      state.templateCategories = action.payload;
    },
    setTemplateFilter(state, action) {
      state.templateFilter = action.payload;
    },
    setSelectedTemplate(state, action) {
      state.selectedTemplate = action.payload;
    },
    setTemplateName(state, action) {
      state.templateName = action.payload;
    },
    setFilteredTemplateCreatives(state, action) {
      state.filteredTemplateCreatives = action.payload;
    },
    setDynamicText(state, action) {
      state.dynamicText = action.payload;
    },
    setActiveDynamicText(state, action) {
      state.activeDynamicText = action.payload;
    },
    setIsApplyAll(state, action) {
      state.isApplyAll = action.payload;
    },
    setIsCreativeLevelFilter(state, action) {
      state.creativeLevelFilter = action.payload;
    },
    updateCreativeFigmaAfterDynamicText(state, action) {
      const { activeDynamicText, creativeIds } = action.payload;
      const texts = activeDynamicText.texts;
      const updatedCreatives = [];
      let i = 0;
      do {
        if (creativeIds.some(id => id === state.scrolledCreatives[i]._id)) {
          let foundCreative = { ...state.scrolledCreatives[i] };
          if (foundCreative) {
            let k = 0;
            do {
              const elIds = Object.keys(foundCreative.figmaObject.node);
              if (texts[k]?.type === "text") {
                let l = 0;
                do {
                  if (foundCreative.figmaObject.node[elIds[l]].hasOwnProperty("name")) {
                    if (cleanString(foundCreative.figmaObject.node[elIds[l]].name) === cleanString(texts[k].name)) {
                      foundCreative.figmaObject.node[elIds[l]].characters = texts[k].value;
                      try {
                        if (texts[k]?.isFeed) {
                          const foundFeed = state.connectedFeed.find(item => item.path === foundCreative.figmaObject.node[elIds[l]].characters);
                          // console.log(foundCreative.size, texts, foundFeed.values[0]);                        
                          if (foundFeed !== undefined && foundFeed !== null) {
                            foundCreative.figmaObject.node[elIds[l]].characters = `<p style='${texts[k].style}'>${foundFeed.values[0]}</p>`;
                          }
                        }
                      } catch (error) {
                        foundCreative.figmaObject.node[elIds[l]].characters = texts[k].value;
                      }
                      break;
                    }
                  }
                  l++;
                } while (l < elIds.length);
              } else if (texts[k]?.type === "image") {
                let l = 0;
                do {
                  if (foundCreative.figmaObject.node[elIds[l]].hasOwnProperty("name")) {
                    if (cleanString(foundCreative.figmaObject.node[elIds[l]].name) === cleanString(texts[k].name)) {
                      if (texts[k].value && texts[k].value !== '' && texts[k].value !== ' ') {
                        foundCreative.figmaObject.node[elIds[l]].bgimages["1x"].data = texts[k].value;
                        foundCreative.figmaObject.node[elIds[l]].bgimages["2x"].data = texts[k].value;
                        break;
                      }
                    }
                  }
                  l++;
                } while (l < elIds.length);
              }
              k++;
            } while (k < texts.length);
            updatedCreatives.push(foundCreative);  
          } else {
            updatedCreatives.push(state.scrolledCreatives[i]);
          }
        } else {
          updatedCreatives.push(state.scrolledCreatives[i]);
        }
        i++;
      } while (i < state.scrolledCreatives.length);
      // state.creatives = updatedCreatives;
      // state.filteredCreatives = updatedCreatives;
      // state.creatives = updatedCreatives;
      state.creatives = state.creatives.map(item => {
        const matchedCreative = updatedCreatives.find(uptdCreative => uptdCreative._id === item._id);
        if (matchedCreative) {
          return matchedCreative;
        } else {
          return item;
        }
      })
    },
    updateCreativeFigmaFonts(state, action) {
      const { creativeIds, fontsCss } = action.payload;
      const updatedCreatives = [];
      let i = 0;
      do {
        if (creativeIds.some(id => id === state.filteredCreatives[i]._id)) {
          let foundCreative = { ...state.filteredCreatives[i] };
          foundCreative.figmaObject.node.frame.fonts = foundCreative.figmaObject.node.frame.fonts + fontsCss;
          updatedCreatives.push(foundCreative);
        } else {
          updatedCreatives.push(state.creatives[i]);
        }
        i++;
      } while (i < state.filteredCreatives.length);
      state.filteredCreatives = updatedCreatives;
      state.creatives = state.creatives.map(item => {
        const matchedCreative = updatedCreatives.find(uptdCreative => uptdCreative._id === item._id);
        if (matchedCreative) {
          return matchedCreative;
        } else {
          return item;
        }
      });
    },
    updateCreativeDynamicLoopAfterFeedDisconnect(state, action) {
      const creativesFromThunk = action.payload.creatives;
      const updatedCreatives = [];
      let i = 0;
      do {
        let creative = state.creatives.find(item => item._id === creativesFromThunk[i]._id);
        creative.figmaObject = creativesFromThunk[i].figmaObject;
        creative.figmaObject.globalLoop.loopElements = [...new Set([...creative.figmaObject.globalLoop.loopElements, ...creative.figmaObject.dynamicLoop.loopElements])];
        creative.figmaObject.dynamicLoop.loopElements = [];
        updatedCreatives.push(creative);
        i++;
      } while (i < creativesFromThunk.length);
      state.creatives = updatedCreatives;
      state.filteredCreatives = updatedCreatives; 
      action.payload.creatives = updatedCreatives;
    },
    // setUploadedFiles(state, action) {
    //   state.uploadedFiles = action.payload;
    // },
    setFilteredFontList(state, action) {
      state.filteredFontList = action.payload;
    },
    setFontNotUploaded(state, action) {
      state.fontNotUploaded = action.payload.fontNotUploaded;
      if (!state.fontNotUploadedName.includes(action.payload.fontNotUploadedName)) {
        state.fontNotUploadedName = [...state.fontNotUploadedName, action.payload.fontNotUploadedName];
      }
    },
    updateFontNotUploaded(state, action) {
      const updatedFontName = state.fontNotUploadedName.filter(item => item !== action.payload)
      state.fontNotUploadedName = updatedFontName;
      if (updatedFontName.length === 0) {
        state.fontNotUploaded = false;
      }
    },
    setSelectedFeedConnection(state, action) {
      state.selectedFeedConnection = action.payload;
    },
    setConnectedFeed(state, action) {
      state.connectedFeed = action.payload;
    },
    setConnectedFeedName(state, action) {
      state.connectedFeedName = action.payload;
    },
    setSelectedTag(state, action) {
      state.selectedTag = action.payload;
    },
    setCampaignLoader(state, action) {
      state.campaignLoader = action.payload;
    },
    setCampaignsReset(state, action) {
      state.allLibraries = null;
      state.allLibrariesErr = false;
      state.allLibrariesErrMsg = null;
      state.campaignUsers = null;
      state.adTypes = null;
      state.filter = {
        query: null,
        publish: null,
        user: null,
        market: null,
        sizes: null,
      };
      state.deleteCampaignId = null;
      state.currentCampaign = null;
      state.versions = null;
      state.currentVersion = null;
      state.versionDetails = {
        _id: "",
        name: "",
        activityType: { name: "", value: "" },
        landingPage: "",
        campaignId: "",
        sizes: []
      };
      state.creatives = null;
      state.filteredCreatives = [];
      state.comments = null;
      state.commentRelationId = null;
      state.commentType = null;
      state.isTemplateLoading = false;
      state.loadedHtmlZip = null;
      state.templateCategories = [];
      state.templates = null;
      state.templateFilter = {
        query: null,
        campaignId: null,
      };
      state.selectedTemplate = null;
      state.templateName = null;
      // Dynamic Text
      state.selectedCreative = null;
      state.dynamicText = null;
      state.activeDynamicText = null;
      state.dynamicFontList = null;
      state.filteredFontList = null;
      state.selectedFeedConnection = null;
      state.connectedFeed = null;
      state.connectedFeedName = null;
      state.selectedTag = null;
      state.campaignLoader = false;
    },
    setCreateCampaignErr(state, action) {
      state.createCampaignErr = true;
      state.createCampaignErrMsg = action.payload;
    },
    resetCreateCampaignErr(state, action) {
      state.createCampaignErr = false;
      state.createCampaignErrMsg = null;
    },
    setSettingsErr(state, action) {
      state.settingsErr = true;
      state.settingsErrMsg = action.payload;
    },
    resetSettingsErr(state, action) {
      state.settingsErr = false;
      state.settingsErrMsg = null;
    },
    setDuplicateErr(state, action) {
      state.duplicateErr = true;
      state.duplicateErrMsg = action.payload;;
    },
    resetDuplicateErr(state, action) {
      state.duplicateErr = false;
      state.duplicateErrMsg = null;
    },
    updateCreativeSetFont(state, action) {
      const finalFonts = action.payload;
      const updatedCreatives = [];
      let i = 0;
      do {
        let finalCreative = { ...state.creatives[i] };
        finalCreative.figmaObject.node.frame.fonts = finalFonts[i];
        updatedCreatives.push(finalCreative);
        i++;
      } while (i < state.creatives.length);
      state.creatives = updatedCreatives;
    },
    checkCreativeFontUploaded(state, action) {
      let fontsUpdated = false;
      let finalFonts = [];
      const updatedCreatives = [];
      if (state.creatives.length > 0 && state.filteredFontList.length > 0) {
        let i = 0;
        do {
          let creative = { ...state.creatives[i] };
          let creativesUpdatedFonts = creative.figmaObject.node.frame.fonts;
          const creativeFonts = creative.figmaObject.node.frame.fonts.split('||');
          if (creativeFonts.length > 0) {
            let k = 0;
            do {
              const fontName = creativeFonts[k].split(':')[0].replaceAll('+', ' ');
              const fontVariant = creativeFonts[k].split(':')[2];
              if (fontName && fontName !== '' && fontName !== ' ') {
                const foundFont = state.filteredFontList.findIndex(item => item.family.toUpperCase().replaceAll('+', ' ').replaceAll(' ', '') === fontName.toUpperCase().replaceAll(' ', ''));
                if (!state.filteredFontList[foundFont]) {
                  state.fontNotUploaded = true;
                  state.fontNotUploadedName = fontName;
                } else if (state.filteredFontList[foundFont] && !state.filteredFontList.find(item => item.family === creativeFonts[k].split(':')[0])) {
                  creative.figmaObject.node.frame.fonts = creativesUpdatedFonts.replaceAll(creativeFonts[k].split(':')[0], state.filteredFontList[foundFont].family);
                  const wrongName = fontName;
                  const trueName = state.filteredFontList[foundFont].family;
                  const node = Object.keys(creative.figmaObject.node);
                  if (node.length > 0) {
                    let m = 0;
                    do {
                      if (creative.figmaObject.node[node[m]].hasOwnProperty('css') &&
                        creative.figmaObject.node[node[m]].css.hasOwnProperty('font-family') &&
                        getStyleValue(`font-family: ${creative.figmaObject.node[node[m]].css['font-family']}`, 'font-family') === wrongName) {
                        creative.figmaObject.node[node[m]].css['font-family'] = trueName;
                      }
                      m++;
                    } while (m < node.length);
                  }
                  fontsUpdated = true;
                }
              }
              k++;
            } while (k < creativeFonts.length);
          }
          updatedCreatives.push(creative);
          i++;
        } while (i < state.creatives.length);
        action.payload.creatives = updatedCreatives;
        action.payload.isUpdated = fontsUpdated;
        state.creatives = updatedCreatives;
        state.filteredCreatives = updatedCreatives;
      }
    },
    updateCreativeNodeKeyFont(state, action) {
      let i = 0;
      const creativeArr = [];
      do {
        let creative = { ...state.creatives[i] };
        const node = Object.keys(creative.figmaObject.node);
        let k = 0;
        do {
          if (node[k] !== 'frame') {
            if (creative.figmaObject.node[node[k]].hasOwnProperty('css') &&
              creative.figmaObject.node[node[k]].css.hasOwnProperty('font-family') &&
              creative.figmaObject.node[node[k]].css['font-family'] === action.payload.wrongName) {
              creative.figmaObject.node[node[k]].css['font-family'] = action.payload.trueName;
            }
          }
          k++;
        } while (k < node.length);
        creativeArr.push(creative);
        i++;
      } while (i < state.creatives.length);
      state.creatives = creativeArr;
    },
    setFontUpdated(state, action) {
      state.fontUpdated = action.payload;
    },
    setDynamicVideoError(state, action) {
      state.dynamicVideoError = action.payload.dynamicVideoError;
      state.dynamicVideoErrorMsg = action.payload.dynamicVideoErrorMsg;
    },
    resetCampaignState(state) {
      state.allLibraries = null
      state.allLibrariesErr = false
      state.allLibrariesErrMsg = null
      state.campaignUsers = null
      state.adTypes = null
      state.filter = {
        query: null,
        publish: null,
        user: null,
        market: null,
        sizes: null,
      }
      state.deleteCampaignId = null
      state.currentCampaign = null
      state.versions = null
      state.currentVersion = null
      state.versionDetails = {
        _id: "",
        name: "",
        activityType: { name: "", value: "" },
        landingPage: "",
        campaignId: "",
        sizes: []
      }
      state.creatives = null
      state.creativesHasMore = true;
      state.creativesLoading = false;
      state.creativesFirstLoad = true;
      state.creativesPage = 1;
      state.filteredCreatives = [];
      state.scrolledCreatives = [];
      state.deleteCreativeId = null
      state.duplicateCreative = null
      state.comments = null
      state.commentRelationId = null
      state.commentType = null
      state.isTemplateLoading = false
      state.loadedHtmlZip = null
      state.templateCategories = []
      state.templates = null
      state.templateFilter = {
        query: null,
        campaignId: null,
      }
      state.selectedTemplate = null
      state.templateName = null
      state.filteredTemplateCreatives = []
      // Dynamic Text
      state.selectedCreative = null
      state.dynamicText = null
      state.activeDynamicText = null
      state.isApplyAll = false
      state.creativeLevelFilter = 'all'
      // state.dynamicFontList = null
      // state.filteredFontList = null
      state.fontNotUploaded = false
      state.fontNotUploadedName = []
      state.fontUpdated = false
      state.dynamicVideoError = false
      state.dynamicVideoErrorMsg = ''
      state.selectedFeedConnection = null
      state.connectedFeed = null
      state.selectedTag = null
      state.campaignLoader = false
      state.createCampaignErr = false
      state.createCampaignErrMsg = null
      state.settingsErr = false
      state.settingsErrMsg = null
      state.duplicateErr = false
      state.duplicateErrMsg = null
    },
    // Facebook
    ...facebook.reducers,
  },
  extraReducers: builder => {
    builder.addCase(getCampaigns.pending, (state, action) => {
      state.allLibrariesShowLoader = true;
    })
    builder.addCase(getCampaigns.fulfilled, (state, action) => {
      state.allLibraries = action.payload;
      state.allLibrariesShowLoader = false;
      state.allLibrariesFirstLoad = false;
    });
    builder.addCase(getCampaignsByPage.fulfilled, (state, action) => {
      state.allLibraries = action.payload;
    });
    builder.addCase(getVersions.fulfilled, (state, action) => {
      state.versions = action.payload;
    });
    builder.addCase(getCreatives.fulfilled, (state, action) => {
      state.creatives = action.payload;
      state.filteredCreatives = action.payload;
    });
    builder.addCase(getAdTypes.fulfilled, (state, action) => {
      state.adTypes = action.payload;
    });
    builder.addCase(getTemplate.fulfilled, (state, action) => {
      state.templates = action.payload;
    });
    builder.addCase(getTemplateCategory.fulfilled, (state, action) => {
      state.templateCategories = action.payload;
    });
    builder.addCase(getDynamicText.fulfilled, (state, action) => {
      state.dynamicText = action.payload;
    });
    builder.addCase(setHtmlCreativesPreview.fulfilled, (state, action) => {
      state.creatives = action.payload;
      state.filteredCreatives = action.payload;
    });
    builder.addCase(getFontList.fulfilled, (state, action) => {
      state.dynamicFontList = action.payload;
      state.filteredFontList = action.payload;
    });
    facebook.getExtraReducers(builder);
  }
});

export const {
  setAllLibrariesPage,
  getAllLibrariesSuccess,
  setAllLibrariesLoading,
  setAllLibrariesHasMore,
  setAllLibrariesFirstLoad,
  setAllLibrariesShowLoader,
  getAllLibrariesErr,
  setCampaignUsers,
  setAdTypes,
  setFilter,
  resetFilter,
  setDeleteCampaignId,
  setCurrentCampaign,
  getVersionsSuccess,
  setCurrentVersion,
  setVersionDetails,
  resetVersionDetails,
  setCreatives,
  setFilteredCreatives,
  setScrolledCreatives,
  updateCreatives,
  updateScrolledCreatives,
  setCreativesHasMore,
  setCreativesLoading,
  setCreativesFirstLoad,
  setCreativesPage,
  setDeleteCreativeId,
  setDuplicateCreative,
  setCreativeOpenedInFigma,
  setSelectedCreative,
  setCreativeApprovement,
  setComments,
  setCommentRelationId,
  setCommentType,
  setCreativeCommentCount,
  setTemplateLoader,
  setTemplates,
  setTemplateCategories,
  setTemplateFilter,
  setSelectedTemplate,
  setTemplateName,
  setFilteredTemplateCreatives,
  setDynamicText,
  setActiveDynamicText,
  setIsApplyAll,
  setIsCreativeLevelFilter,
  updateCreativeFigmaAfterDynamicText,
  updateCreativeFigmaFonts,
  updateCreativeDynamicLoopAfterFeedDisconnect,
  setFilteredFontList,
  setFontNotUploaded,
  updateFontNotUploaded,
  // setUploadedFiles,
  setSelectedFeedConnection,
  setConnectedFeed,
  setConnectedFeedName,
  setSelectedTag,
  setCampaignLoader,
  setCampaignsReset,
  setCreateCampaignErr,
  resetCreateCampaignErr,
  setSettingsErr,
  resetSettingsErr,
  setDuplicateErr,
  resetDuplicateErr,
  checkCreativeFontUploaded,
  updateCreativeSetFont,
  updateCreativeNodeKeyFont,
  setFontUpdated,
  setDynamicVideoError,
  resetCampaignState,
  // Facebook
  setProperties,
  setSelectedFacebookProperty,
  setFacebookPropertyFeed,
  setFacebookId,
  setIsFacebookProperties,
  setFacebookStatus,
  setFacebookCreativeIds,
  resetFacebook,
  setFacebookPublishMessage
} = campaignSlice.actions;

export default campaignSlice.reducer